import React, { useState, useEffect } from 'react';
import { FaEnvelope, FaBell, FaCheckCircle, FaArrowRight, FaTimes, FaGift, FaRocket } from 'react-icons/fa';
import styled from 'styled-components';

const MessageContainer = styled.div`
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 1000;
  max-width: 400px;
  animation: slideInRight 0.3s ease-out;

  @keyframes slideInRight {
    from {
      opacity: 0;
      transform: translateX(100%);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  @media (max-width: 768px) {
    top: 10px;
    right: 10px;
    left: 10px;
    max-width: none;
  }
`;

const MessageCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  border-left: 4px solid ${props => props.type === 'success' ? '#48bb78' : props.type === 'info' ? '#4299e1' : '#0078D4'};
  margin-bottom: 15px;
  position: relative;
`;

const MessageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 10px;
`;

const MessageIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${props => props.type === 'success' ? '#48bb78' : props.type === 'info' ? '#4299e1' : '#0078D4'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.1rem;
  margin-right: 15px;
`;

const MessageContent = styled.div`
  flex: 1;
`;

const MessageTitle = styled.h4`
  color: #2d3748;
  font-size: 1.1rem;
  margin: 0 0 5px 0;
`;

const MessageText = styled.p`
  color: #4a5568;
  font-size: 0.9rem;
  line-height: 1.4;
  margin: 0 0 15px 0;
`;

const MessageActions = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
`;

const MessageButton = styled.button`
  background: ${props => props.primary ? '#0078D4' : 'transparent'};
  color: ${props => props.primary ? 'white' : '#0078D4'};
  border: 1px solid #0078D4;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 5px;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.primary ? '#005a9e' : '#f7fafc'};
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #a0aec0;
  cursor: pointer;
  padding: 5px;
  font-size: 1rem;
  
  &:hover {
    color: #2d3748;
  }
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 4px;
  background: #e2e8f0;
  border-radius: 2px;
  overflow: hidden;
  margin-top: 15px;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: linear-gradient(90deg, #0078D4, #00B294);
  width: ${props => props.progress}%;
  transition: width 0.3s ease;
`;

const EmailPreview = styled.div`
  background: #f7fafc;
  border-radius: 8px;
  padding: 15px;
  margin: 15px 0;
  border: 1px solid #e2e8f0;
`;

const EmailSubject = styled.div`
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 8px;
`;

const EmailContent = styled.div`
  color: #4a5568;
  font-size: 0.9rem;
  line-height: 1.4;
`;

const WelcomeMessages = ({ user, onCompleteTask, onDismiss }) => {
  const [messages, setMessages] = useState([]);
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);

  useEffect(() => {
    // Initialize welcome messages based on user state
    const initialMessages = [
      {
        id: 'welcome',
        type: 'info',
        title: 'Welcome to Rundoit! 🎉',
        text: 'We\'re excited to help you boost your productivity. Let\'s get you started with your first tasks!',
        icon: <FaRocket />,
        actions: [
          { text: 'Get Started', primary: true, action: 'start' }
        ],
        showProgress: false
      },
      {
        id: 'first-task',
        type: 'info',
        title: 'Great start! 🚀',
        text: 'You\'ve created your first task. Now let\'s complete it to see the magic happen!',
        icon: <FaCheckCircle />,
        actions: [
          { text: 'Complete Task', primary: true, action: 'complete' }
        ],
        showProgress: true,
        progress: 33
      },
      {
        id: 'second-task',
        type: 'success',
        title: 'You\'re on fire! 🔥',
        text: 'One task down! Complete 2 more to unlock your productivity superpowers.',
        icon: <FaGift />,
        actions: [
          { text: 'Keep Going', primary: true, action: 'continue' }
        ],
        showProgress: true,
        progress: 66
      },
      {
        id: 'third-task',
        type: 'success',
        title: 'Productivity Master! 🏆',
        text: 'Amazing! You\'ve completed 3 tasks. You\'re now ready to tackle any challenge!',
        icon: <FaCheckCircle />,
        actions: [
          { text: 'Explore Features', primary: true, action: 'explore' }
        ],
        showProgress: true,
        progress: 100
      }
    ];

    setMessages(initialMessages);
  }, [user]);

  const handleAction = (action) => {
    switch (action) {
      case 'start':
        // Trigger onboarding tour or template selection
        break;
      case 'complete':
        // Mark first task as complete
        onCompleteTask();
        break;
      case 'continue':
        // Encourage to continue
        break;
      case 'explore':
        // Show feature highlights
        break;
      default:
        break;
    }
  };

  const handleDismiss = (messageId) => {
    setMessages(prev => prev.filter(msg => msg.id !== messageId));
    onDismiss(messageId);
  };

  const currentMessage = messages[currentMessageIndex];

  if (!currentMessage) return null;

  return (
    <MessageContainer>
      <MessageCard type={currentMessage.type}>
        <MessageHeader>
          <MessageIcon type={currentMessage.type}>
            {currentMessage.icon}
          </MessageIcon>
          <MessageContent>
            <MessageTitle>{currentMessage.title}</MessageTitle>
            <MessageText>{currentMessage.text}</MessageText>
            
            {currentMessage.id === 'welcome' && (
              <EmailPreview>
                <EmailSubject>📧 Welcome Email Sent!</EmailSubject>
                <EmailContent>
                  We've sent you a welcome email with tips and tricks to get the most out of Rundoit. 
                  Check your inbox!
                </EmailContent>
              </EmailPreview>
            )}
            
            <MessageActions>
              {currentMessage.actions.map((action, index) => (
                <MessageButton
                  key={index}
                  primary={action.primary}
                  onClick={() => handleAction(action.action)}
                >
                  {action.text}
                  <FaArrowRight />
                </MessageButton>
              ))}
            </MessageActions>
            
            {currentMessage.showProgress && (
              <ProgressBar>
                <ProgressFill progress={currentMessage.progress} />
              </ProgressBar>
            )}
          </MessageContent>
          <CloseButton onClick={() => handleDismiss(currentMessage.id)}>
            <FaTimes />
          </CloseButton>
        </MessageHeader>
      </MessageCard>
    </MessageContainer>
  );
};

export default WelcomeMessages;
